'use client'

import { InfoBarAction } from '@/app/[locale]/product/[slug]/_components/info-bar/info-action'
import { InfoBarColorShort } from '@/app/[locale]/product/[slug]/_components/info-bar/info-color-short'
import { InfoProductShort } from '@/app/[locale]/product/[slug]/_components/info-bar/info-product-short'
import { InfoRatingShort } from '@/app/[locale]/product/[slug]/_components/info-bar/info-rating-short'
import { PRODUCT_SIZE } from '@/lib/constants/size'
import { ProductDetailFormatted } from '@/types/product-detail'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'
import { InfoSizesShort } from './info-sizes-short'
import { InfoSizesShortCombo } from './info-sizes-short-combo'

type Props = {
  product?: ProductDetailFormatted
  sizeChart?: string
  isStopSale?: boolean
  isGiveaway?: boolean
  isDisabled?: boolean
  preview?: string
}

export const InfoBarDesktop = ({
  product: productProp,
  sizeChart: sizeChartProp,
  isStopSale: isStopSaleProp,
  isGiveaway: isGiveawayProp,
  isDisabled: isDisabledProp,
  preview: previewProp,
}: Props) => {
  const { product: safeProduct } = useProductDetailSafe()
  const { productData } = useProductDetailStore()
  const searchParams = useSearchParams()

  const product = productProp || productData
  const sizeChart =
    sizeChartProp || (safeProduct ? PRODUCT_SIZE[safeProduct.sizechart] : '')
  const preview = previewProp ?? (searchParams.get('preview') || '')
  const isStopSale = isStopSaleProp ?? safeProduct?.product_stoppage
  const isGiveaway = isGiveawayProp ?? safeProduct?.giveaway_all
  const isDisabled =
    isDisabledProp ??
    !Object.entries(productData?.mapped_variants || {}).length

  const [showInfoBar, setShowInfoBar] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const productDescription = document.getElementById(
        'product-detail-policy'
      )
      if (productDescription) {
        const rect = productDescription.getBoundingClientRect()
        setShowInfoBar(rect.top <= 0)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('quickbuy:toggle', { detail: { visible: showInfoBar } })
    )
    return () => {
      window.dispatchEvent(
        new CustomEvent('quickbuy:toggle', { detail: { visible: false } })
      )
    }
  }, [showInfoBar])

  if (!showInfoBar || !product) return null

  return (
    <div className='fixed left-0 top-0 z-[60] !mt-0 w-full max-w-full bg-light shadow-[0px_1px_4px_0px_hsla(0,_0%,_0%,_0.08),_0px_0px_24px_0px_hsla(0,_0%,_0%,_0.06)] max-lg:hidden lg:px-4 2xl:px-0'>
      <div className='flex items-stretch gap-2 2xl:container 2xl:gap-6'>
        <InfoProductShort product={product} />
        <InfoRatingShort product={product} />
        {product?.is_setdo ? (
          <InfoSizesShortCombo />
        ) : (
          <>
            <InfoBarColorShort />
            <InfoSizesShort
              sizeChart={sizeChart}
              gender_type={product.gender_type}
              size_guide={product.size_guide}
            />
          </>
        )}
        {preview ? null : (
          <InfoBarAction
            isStopSale={isStopSale}
            isGiveaway={isGiveaway}
            isDisabled={isDisabled}
          />
        )}
      </div>
    </div>
  )
}
